// bmp-modal-focus-scope.js - keep plain Tab navigation inside the active
// native modal dialog.
//
// WHAT: wraps Tab at the final currently tabbable element and Shift+Tab at
// the first for the dialog:modal that owns the keyboard event. Tabbables are
// recomputed on every Tab press so morphs and cart/search/quick-add updates do
// not leave a stale boundary.
//
// SCOPE: modal dialogs only. This module does not handle Escape, close a
// dialog, lock scrolling, restore focus, install a focusin redirect, or call
// core focus-trap APIs. Non-modal theme drawers therefore remain exclusively
// under bmp-drawer-focus-scope.js.

/**
 * Returns the composed parent so visibility/inert checks can cross an open
 * shadow-root boundary.
 * @param {Element} element
 * @returns {Element | null}
 */
function getComposedParent(element) {
  if (element.assignedSlot) return element.assignedSlot;
  if (element.parentElement) return element.parentElement;

  const root = element.getRootNode();
  return root instanceof ShadowRoot ? root.host : null;
}

/**
 * Whether an element is rendered and remains eligible for sequential focus.
 * @param {Element & { tabIndex: number }} element
 * @param {HTMLDialogElement} dialog
 * @returns {boolean}
 */
function isTabbable(element, dialog) {
  if (element.tabIndex < 0 || element.matches(':disabled')) return false;
  if (element.getClientRects().length === 0) return false;

  for (let current = element; current; current = getComposedParent(current)) {
    if (current.hasAttribute('hidden') || current.hasAttribute('inert')) return false;
    if (current.getAttribute('aria-hidden') === 'true') return false;

    const style = getComputedStyle(current);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse' ||
      style.contentVisibility === 'hidden'
    ) {
      return false;
    }

    if (current === dialog) return true;
    if (current instanceof HTMLDialogElement && current.matches(':modal')) return false;
  }

  return false;
}

/**
 * Returns rendered elements in composed-tree order, including open shadow
 * roots and assigned slot content. Descendant modal dialogs are deliberately
 * omitted so a parent modal never counts another modal's controls.
 * @param {Element | ShadowRoot} root
 * @param {HTMLDialogElement} dialog
 * @param {Element[]} elements
 */
function collectComposedElements(root, dialog, elements) {
  let children;

  if (root instanceof HTMLSlotElement) {
    const assigned = root.assignedElements({ flatten: true });
    children = assigned.length > 0 ? assigned : Array.from(root.children);
  } else if (root instanceof Element && root.shadowRoot) {
    children = Array.from(root.shadowRoot.children);
  } else {
    children = Array.from(root.children);
  }

  for (const child of children) {
    if (child instanceof HTMLDialogElement && child !== dialog && child.matches(':modal')) continue;

    elements.push(child);
    collectComposedElements(child, dialog, elements);
  }
}

/**
 * Resolves current tabbables using sequential-focus order: positive tabindex
 * values first (ascending), followed by zero/default tabindex in composed DOM
 * order.
 * @param {HTMLDialogElement} dialog
 * @returns {Element[]}
 */
function getTabbableElements(dialog) {
  /** @type {Element[]} */
  const elements = [];
  collectComposedElements(dialog, dialog, elements);

  const tabbables = elements
    .filter(
      /** @returns {element is Element & { tabIndex: number }} */
      (element) => 'tabIndex' in element && isTabbable(element, dialog)
    )
    .map((element, order) => ({ element, order, tabIndex: element.tabIndex }))
    .sort((left, right) => {
      const leftOrder = left.tabIndex > 0 ? left.tabIndex : Number.MAX_SAFE_INTEGER;
      const rightOrder = right.tabIndex > 0 ? right.tabIndex : Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder || left.order - right.order;
    })
    .map(({ element }) => element);

  const radioGroupStops = new Map();

  for (const element of tabbables) {
    if (!(element instanceof HTMLInputElement) || element.type !== 'radio' || element.name === '') continue;

    const group = tabbables.filter(
      (candidate) =>
        candidate instanceof HTMLInputElement &&
        candidate.type === 'radio' &&
        candidate.name === element.name &&
        candidate.form === element.form &&
        candidate.getRootNode() === element.getRootNode()
    );
    const stop = group.find((candidate) => candidate.checked) ?? group[0];

    for (const radio of group) radioGroupStops.set(radio, stop);
  }

  return tabbables.filter((element) => !radioGroupStops.has(element) || radioGroupStops.get(element) === element);
}

/**
 * Returns the innermost modal dialog from the event's composed path. That is
 * the currently focused modal when dialogs are nested or stacked.
 * @param {KeyboardEvent} event
 * @returns {HTMLDialogElement | null}
 */
function getActiveModalDialog(event) {
  for (const target of event.composedPath()) {
    if (target instanceof HTMLDialogElement && target.open && target.matches(':modal')) return target;
  }

  return null;
}

/**
 * Returns true when focus is the candidate or is retargeted from within its
 * composed subtree.
 * @param {Element} candidate
 * @param {EventTarget | null} focusedTarget
 * @returns {boolean}
 */
function ownsFocusedTarget(candidate, focusedTarget) {
  if (!(focusedTarget instanceof Element)) return false;

  for (let current = focusedTarget; current; current = getComposedParent(current)) {
    if (current === candidate) return true;
  }

  return false;
}

/** @param {KeyboardEvent} event */
function containModalTabBoundary(event) {
  if (
    event.key !== 'Tab' ||
    event.defaultPrevented ||
    event.isComposing ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey
  ) {
    return;
  }

  const dialog = getActiveModalDialog(event);
  if (!dialog) return;

  const tabbables = getTabbableElements(dialog);
  if (tabbables.length === 0) return;

  const first = tabbables[0];
  const last = tabbables[tabbables.length - 1];

  if (tabbables.length === 1) {
    event.preventDefault();
    first.focus();
    return;
  }

  const focusedTarget = event.composedPath()[0] ?? document.activeElement;
  const atFirst = ownsFocusedTarget(first, focusedTarget);
  const atLast = ownsFocusedTarget(last, focusedTarget);

  if (event.shiftKey ? atFirst || focusedTarget === dialog : atLast) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }
}

document.addEventListener('keydown', containModalTabBoundary, true);
